import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FlightButton, FlightCheckbox, FlightDropdown, FlightTooltip, getIcon } from '@flybits/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppSelector as useSelector } from 'hooks/reduxHooks';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { EVENT_KEYS } from 'types/events';
import { CtxDataFile } from 'interface/file/file.interface';
import { PaginationResponse } from 'interface/shared/api.interface';
import FileAPI from 'services/api/file.api';
import { CtxFileStatus, FILE_STATUS } from 'pages/ExperienceCanvas/types';
import FileIcon from 'pages/ExperienceCanvas/assets/icons/FileIcon';
import InfoIcon from 'pages/ExperienceCanvas/assets/icons/InfoIcon';
import PlayIcon from 'pages/ExperienceCanvas/assets/icons/PlayIcon';
import RefreshIcon from 'pages/ExperienceCanvas/assets/icons/RefreshIcon';
import UploadContextDataIcon from 'pages/ExperienceCanvas/assets/icons/UploadContextDataIcon';
import { ACTIVE_STATUSES } from 'store/journey/journey.type';
import { showSnackbar } from 'store/snackbar/snackbar.action';
import { FILE_STATUSES } from '../constants';
import LoadingIcon from 'components/Shared/LoadingIcon/LoadingIcon';
import './FileUploadDropdown.scss';
import ProcessContextFileButton from '../ProcessContextFileButton/ProcessContextFileButton';
import { REFETCH_INTERVALS } from 'constants/shared';

type TFileUploadDropdown = {
  title: string;
  onViewAllClick: () => void;
};

const MAIN_CLASS = 'file-upload-dropdown';
const CLASSES = {
  COLLAPSED_TRIGGER: `${MAIN_CLASS}__collapsed-trigger`,
  ITEM_STATUS_LABEL: `${MAIN_CLASS}__item-status-label`,
  ITEM_STATUS_LABEL_READY: `${MAIN_CLASS}__item-status-label ${MAIN_CLASS}__item-status-label--ready`,
  ITEM_STATUS_LABEL_ROUNDED: `${MAIN_CLASS}__item-status-label ${MAIN_CLASS}__item-status-label--is-rounded`,
  STATUS_ACTIONS: `${MAIN_CLASS}__status-actions`,
  STATUS_CHEVRON: `${MAIN_CLASS}__status-chevron`,
  DROPDOWN: `${MAIN_CLASS}__dropdown`,
  DROPDOWN_CONTENT: `${MAIN_CLASS}__dropdown-content`,
  DROPDOWN_META: `${MAIN_CLASS}__dropdown-meta`,
  DROPDOWN_META_TIMER: `${MAIN_CLASS}__dropdown-meta__timer`,
  DROPDOWN_META_REFRESH: `${MAIN_CLASS}__dropdown-meta__refresh`,
  DROPDOWN_COMPLETED: `${MAIN_CLASS}__dropdown-completed`,
  DROPDOWN_DRAFT_BANNER: `${MAIN_CLASS}__draft-banner`,
  DROPDOWN_READY_BANNER: `${MAIN_CLASS}__ready-banner`,
  DROPDOWN_INFO_BANNER: `${MAIN_CLASS}__info-banner`,
  DROPDOWN_READY_HEADER: `${MAIN_CLASS}__ready-header`,
  DROPDOWN_READY_TITLE: `${MAIN_CLASS}__ready-header__title`,
  DROPDOWN_PROCESS_BTN: `${MAIN_CLASS}__ready-header__process-btn`,
  DROPDOWN_PROCESSING_HEADER: `${MAIN_CLASS}__processing-header`,
  DROPDOWN_PROCESSING_TITLE: `${MAIN_CLASS}__processing-header__title`,
  DROPDOWN_PAUSE_BTN: `${MAIN_CLASS}__processing-header__pause-btn`,
  UPLOADED_SECTION_HEADER: `${MAIN_CLASS}__uploaded-section-header`,
  FILE_WITH_CHECKBOX: `${MAIN_CLASS}__file-with-checkbox`,
  VIEW_ALL_LINK: `${MAIN_CLASS}__view-all__link`,
  FILE_LIST_WRAPPER: `${MAIN_CLASS}__file-list-wrapper`,
  FILE_GROUP: `${MAIN_CLASS}__file-group`,
  DISCLAIMER: `${MAIN_CLASS}__file-group__disclaimer`,
  FILE: `${MAIN_CLASS}__file`,
  FILE_INDICATOR: `${MAIN_CLASS}__file-indicator`,
  FILE_INDICATOR_ACTIVE: `${MAIN_CLASS}__file-indicator--active`,
  FILE_INDICATOR_QUEUED: `${MAIN_CLASS}__file-indicator--queued`,
  EMPTY_STATE: `${MAIN_CLASS}__empty-state`,
  VIEW_ALL: `${MAIN_CLASS}__view-all`,
};

const PROCESSING_QUEUED = 'PROCESSING_QUEUED' as const;

type InProgressFileStatus = typeof FILE_STATUS.PROCESSING | typeof PROCESSING_QUEUED;

const IN_PROGRESS_STATUSES: readonly InProgressFileStatus[] = [FILE_STATUS.PROCESSING, PROCESSING_QUEUED];

const perPage = 10;

function isInProgressStatus(status: string): status is InProgressFileStatus {
  return (IN_PROGRESS_STATUSES as readonly string[]).includes(status);
}

function getFileCounts(files: CtxDataFile[]) {
  const total = files.length;
  const processed = files.filter(
    (f) => f.status === FILE_STATUS.PROCESSING_SUCCESS || f.status === FILE_STATUS.PROCESSING_FAILED,
  ).length;
  const queued = files.filter((f) => f.status === PROCESSING_QUEUED).length;
  const allCompleted = total > 0 && files.every((f) => f.status === FILE_STATUS.PROCESSING_SUCCESS);
  const hasProcessing = files.some((f) => isInProgressStatus(f.status));
  return { total, processed, queued, allCompleted, hasProcessing };
}

function formatCompletedTimestamp(epochSeconds: number): string {
  const d = new Date(epochSeconds * 1000);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = String(d.getFullYear()).slice(-2);
  const hours = d.getHours() % 12 || 12;
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = d.getHours() >= 12 ? 'pm' : 'am';
  return `${month}/${day}/${year} ${hours}:${minutes}${ampm}`;
}

function getTooltipText(files: CtxDataFile[] | undefined): string {
  if (!files || files.length === 0) return 'No files uploaded';
  const { allCompleted, queued, hasProcessing } = getFileCounts(files);
  if (allCompleted) return 'Processing complete';
  if (hasProcessing) {
    const activelyProcessing = files.filter((f) => f.status === FILE_STATUS.PROCESSING).length;
    if (activelyProcessing && queued) {
      return `Processing in progress. ${queued} file${queued !== 1 ? 's' : ''} in queue`;
    }
    if (activelyProcessing) return 'Processing in progress';
    return `${queued} file${queued !== 1 ? 's' : ''} in queue`;
  }
  const hasError = files.some(
    (f) => f.status === FILE_STATUS.PROCESSING_FAILED || f.status === FILE_STATUS.UPLOADING_FAILED,
  );
  if (hasError) return FILE_STATUSES.PROCESSING_FAILED.text;
  return FILE_STATUSES[files[0]?.status]?.text ?? FILE_STATUSES.UNKNOWN.text;
}

function getReadyToProcessTooltipText(count: number): string {
  return count === 1 ? '1 file is ready to process' : `${count} files are ready to process`;
}

const FileUploadDropdown: React.FC<TFileUploadDropdown> = ({ title, onViewAllClick }) => {
  const fileAPI = useMemo(() => new FileAPI(), []);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { flags } = useFeatureFlag();
  const isQueueV2 = !!flags['tx_context-data-queue-v2'];

  const [isOpen, setIsOpen] = useState(false);
  const [secondsAgo, setSecondsAgo] = useState(0);
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());
  const [isProcessingSelected, setIsProcessingSelected] = useState(false);
  const lastFetchTimeRef = useRef<number>(Date.now());
  const filesUUID = useSelector((state) => state.te.journey.metadata?.filesUUID || '');
  const journeyStatus = useSelector((state) => state.te.journey.status);
  const isJourneyActiveOrScheduled = ACTIVE_STATUSES.includes(journeyStatus);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const fetchCtxDataFiles = async () =>
    await fileAPI.getAllCtxDataFiles({
      offset: 0,
      limit: perPage,
      tag: filesUUID,
    });

  const { data: res, refetch, dataUpdatedAt } = useQuery<PaginationResponse<CtxDataFile>>({
    queryKey: ['ctx-data-file', perPage, filesUUID],
    queryFn: () => fetchCtxDataFiles(),
    keepPreviousData: true,
    refetchInterval: REFETCH_INTERVALS.FILE_PROCESSING,
    enabled: !!filesUUID,
  });

  useEffect(() => {
    if (dataUpdatedAt) {
      lastFetchTimeRef.current = dataUpdatedAt;
      setSecondsAgo(0);
    }
  }, [dataUpdatedAt]);

  const handleRefreshNow = useCallback(() => {
    refetch();
  }, [refetch]);

  const sortedFiles = useMemo(() => {
    if (!res?.data) return [];
    return [...res.data].sort(
      (a, b) =>
        (FILE_STATUSES[a.status]?.priority ?? FILE_STATUSES.UNKNOWN.priority) -
        (FILE_STATUSES[b.status]?.priority ?? FILE_STATUSES.UNKNOWN.priority),
    );
  }, [res?.data]);

  const fileCounts = useMemo(() => getFileCounts(sortedFiles), [sortedFiles]);

  const unprocessedFiles = useMemo(
    () => sortedFiles.filter((f) => f.status === FILE_STATUS.UPLOADED),
    [sortedFiles],
  );

  const isDraftUploadedState =
    isQueueV2 && unprocessedFiles.length > 0 && unprocessedFiles.length === sortedFiles.length && !isJourneyActiveOrScheduled;

  const isReadyToProcessState =
    isQueueV2 && unprocessedFiles.length > 0 && isJourneyActiveOrScheduled && unprocessedFiles.length === sortedFiles.length;

  const showV2ProcessingUi = isQueueV2 && fileCounts.hasProcessing;
  const showV2CompletedUi = isQueueV2 && fileCounts.allCompleted;
  const showV2LegacyFallback =
    isQueueV2 &&
    sortedFiles.length > 0 &&
    !isDraftUploadedState &&
    !isReadyToProcessState &&
    !showV2ProcessingUi &&
    !showV2CompletedUi;

  const showLegacyGroupedList = !isQueueV2 || showV2LegacyFallback;

  useEffect(() => {
    if (!showV2ProcessingUi || !isOpen) return;
    const interval = setInterval(() => {
      setSecondsAgo(Math.floor((Date.now() - lastFetchTimeRef.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [showV2ProcessingUi, isOpen]);

  const highestPriorityStatus = sortedFiles[0]?.status;
  const statusInfo = highestPriorityStatus
    ? FILE_STATUSES[highestPriorityStatus] ?? FILE_STATUSES.UNKNOWN
    : { bgColor: '', fgColor: '', text: 'No files uploaded', Icon: () => null };

  const headerStatusText = isDraftUploadedState
    ? 'Process file(s)'
    : isReadyToProcessState
    ? FILE_STATUSES.UPLOADED.title
    : fileCounts.hasProcessing
    ? `Processing (${fileCounts.processed}/${fileCounts.total})`
    : showV2CompletedUi
    ? 'Completed'
    : statusInfo.text;

  const headerPillStyle = isDraftUploadedState
    ? { backgroundColor: '#fdf9d4', color: '#505a72' }
    : isReadyToProcessState
    ? undefined
    : { backgroundColor: statusInfo.bgColor, color: statusInfo.fgColor };

  const MainStatusIcon = statusInfo.Icon;

  useEffect(() => {
    if (isReadyToProcessState) {
      setSelectedFileIds(new Set(unprocessedFiles.map((f) => f.id)));
    }
  }, [isReadyToProcessState, unprocessedFiles]);

  const handleToggleFileSelection = (fileId: string) => {
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  };

  const handleProcessSelectedFiles = async () => {
    if (selectedFileIds.size === 0) return;
    setIsProcessingSelected(true);
    const fileIds = [...selectedFileIds];
    const results = await Promise.allSettled(fileIds.map((fileId) => fileAPI.processFileById(fileId)));
    const failedCount = results.filter((r) => r.status === 'rejected').length;
    const successCount = results.length - failedCount;

    if (successCount > 0) {
      dispatch(
        showSnackbar({
          content:
            failedCount > 0
              ? `${successCount} file${successCount !== 1 ? 's' : ''} queued for processing. ${failedCount} failed.`
              : 'Your files will be processed shortly',
          type: failedCount > 0 ? 'warning' : 'success',
        }),
      );
      await queryClient.invalidateQueries(['ctx-data-file']);
    } else {
      dispatch(
        showSnackbar({
          content: `Failed to process ${failedCount} file${failedCount !== 1 ? 's' : ''}. Please try again.`,
          type: 'error',
        }),
      );
    }
    setIsProcessingSelected(false);
  };

  const completedTimestamp = useMemo(() => {
    if (!fileCounts.allCompleted || !sortedFiles.length) return '';
    const latestCompletedAt = Math.max(...sortedFiles.map((f) => f.completedAt || 0));
    return latestCompletedAt > 0 ? formatCompletedTimestamp(latestCompletedAt) : '';
  }, [fileCounts.allCompleted, sortedFiles]);

  const tooltipText = isQueueV2
    ? isReadyToProcessState
      ? getReadyToProcessTooltipText(unprocessedFiles.length)
      : getTooltipText(res?.data)
    : undefined;

  const openDropdown = () => {
    setIsOpen(true);
  };

  const closeDropdown = (focusAfterClose = false) => {
    setIsOpen(false);
    if (focusAfterClose) triggerRef.current?.focus();
  };

  const handleViewAllClick = () => {
    closeDropdown();
    onViewAllClick();
  };

  useEffect(() => {
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === EVENT_KEYS.ESCAPE) {
        closeDropdown(true);
      }
    };

    document.addEventListener('keyup', handleKeyUp);

    return () => document.removeEventListener('keyup', handleKeyUp);
  }, []);

  const triggerButton = (
    <button
      type="button"
      ref={triggerRef}
      onClick={openDropdown}
      onKeyUp={(evt: React.KeyboardEvent<HTMLButtonElement>) => {
        if (evt.key === EVENT_KEYS.ENTER) openDropdown();
      }}
    >
      <UploadContextDataIcon />
      {title}
      {isReadyToProcessState ? (
        <div className={CLASSES.STATUS_ACTIONS}>
          <div className={CLASSES.ITEM_STATUS_LABEL_READY}>
            {headerStatusText}
            <PlayIcon fill="#0851df" ariaHidden />
          </div>
          <span className={CLASSES.STATUS_CHEVRON} aria-hidden>
            {getIcon(isOpen ? 'infoFilled' : 'baselineKeyboardArrowRight', {})}
          </span>
        </div>
      ) : (
        <div className={CLASSES.ITEM_STATUS_LABEL} style={headerPillStyle}>
          {!isDraftUploadedState && <MainStatusIcon />}
          {headerStatusText}
          {isDraftUploadedState && getIcon('lock', {})}
          {getIcon(isOpen ? 'infoFilled' : 'baselineKeyboardArrowRight', {})}
        </div>
      )}
    </button>
  );

  return (
    <div className={MAIN_CLASS}>
      <div className={CLASSES.COLLAPSED_TRIGGER}>
        <FlightDropdown
          autoFocus // eslint-disable-line jsx-a11y/no-autofocus
          className={CLASSES.DROPDOWN}
          maxWidth="388px"
          isActive={isOpen}
          trigger={
            isQueueV2 && tooltipText ? (
              <FlightTooltip direction="bottom" description={tooltipText} isEnabled={!isOpen}>
                {triggerButton}
              </FlightTooltip>
            ) : (
              triggerButton
            )
          }
          isControlledByIsActive
          handleClickOutside={() => closeDropdown()}
          maxHeight="420px"
        >
          <div className={CLASSES.DROPDOWN_CONTENT}>
            {showV2ProcessingUi && (
              <div className={CLASSES.DROPDOWN_META}>
                <span className={CLASSES.DROPDOWN_META_TIMER}>
                  Last updated: {secondsAgo} second{secondsAgo !== 1 ? 's' : ''} ago
                </span>
                <button
                  type="button"
                  className={CLASSES.DROPDOWN_META_REFRESH}
                  onClick={handleRefreshNow}
                  aria-label="Refresh now"
                >
                  Refresh now <RefreshIcon ariaHidden />
                </button>
              </div>
            )}
            {!isQueueV2 && <h4>{title}</h4>}
            {showV2CompletedUi && completedTimestamp && (
              <div className={CLASSES.DROPDOWN_COMPLETED}>Completed: {completedTimestamp}</div>
            )}
            {isDraftUploadedState && (
              <div className={CLASSES.DROPDOWN_DRAFT_BANNER}>
                <InfoIcon fill="#505a72" />
                <div>
                  <strong>Launch this experience to unlock file processing</strong>
                  <br />
                  Once you launch this experience, you will be able to process your uploaded files
                </div>
              </div>
            )}
            {isReadyToProcessState && (
              <div className={CLASSES.DROPDOWN_READY_BANNER}>
                {getIcon('infoFilled', {})}
                <div>
                  <strong>Confirm your selection</strong>
                  <br />
                  Once processing begins, the queue order is final and files cannot be removed.
                </div>
              </div>
            )}
            {showV2ProcessingUi && (
              <div className={CLASSES.DROPDOWN_INFO_BANNER}>
                {getIcon('infoFilled', {})}
                <div>
                  <strong>Processing one by one.</strong>
                  <br />
                  {"You're all set! Your files are queued and will process automatically."}
                </div>
              </div>
            )}
            <div className={CLASSES.FILE_LIST_WRAPPER}>
              {showV2CompletedUi && sortedFiles.length > 0 && (
                <div className={CLASSES.FILE_GROUP}>
                  <h5 style={{ color: FILE_STATUSES.PROCESSING_SUCCESS.fgColor }}>
                    {sortedFiles.length} file{sortedFiles.length !== 1 ? 's' : ''} processed
                  </h5>
                  {sortedFiles.map((file) => (
                    <div key={file.id} className={CLASSES.FILE}>
                      <FILE_STATUSES.PROCESSING_SUCCESS.Icon />
                      <FileIcon />
                      {file.name}
                    </div>
                  ))}
                </div>
              )}
              {isDraftUploadedState && (
                <div className={CLASSES.FILE_GROUP}>
                  <h5 className={CLASSES.UPLOADED_SECTION_HEADER}>Uploaded files</h5>
                  {sortedFiles.map((file) => (
                    <div key={file.id} className={CLASSES.FILE}>
                      <FileIcon />
                      {file.name}
                    </div>
                  ))}
                </div>
              )}
              {isReadyToProcessState && (
                <>
                  <div className={CLASSES.DROPDOWN_READY_HEADER}>
                    <span className={CLASSES.DROPDOWN_READY_TITLE}>
                      {unprocessedFiles.length} unprocessed file{unprocessedFiles.length !== 1 ? 's' : ''}
                    </span>
                    <FlightButton
                      className={CLASSES.DROPDOWN_PROCESS_BTN}
                      theme="primary"
                      label={`Process selected files (${selectedFileIds.size})`}
                      onClick={handleProcessSelectedFiles}
                      disabled={selectedFileIds.size === 0 || isProcessingSelected}
                    />
                  </div>
                  {unprocessedFiles.map((file) => (
                    <div key={file.id} className={CLASSES.FILE_WITH_CHECKBOX}>
                      <FlightCheckbox
                        checkState={selectedFileIds.has(file.id) ? 'SELECTED' : 'UNSELECTED'}
                        onSelect={() => handleToggleFileSelection(file.id)}
                      />
                      <span>{file.name}</span>
                    </div>
                  ))}
                </>
              )}
              {showV2ProcessingUi && (
                <>
                  <div className={CLASSES.DROPDOWN_PROCESSING_HEADER}>
                    <span className={CLASSES.DROPDOWN_PROCESSING_TITLE}>
                      Processing {fileCounts.total} file{fileCounts.total !== 1 ? 's' : ''}
                    </span>
                    <FlightButton
                      className={CLASSES.DROPDOWN_PAUSE_BTN}
                      theme="secondary"
                      label="Pause"
                      iconRight="pauseCircleFilled"
                      disabled
                    />
                  </div>
                  {sortedFiles.map((file) => {
                    const isActive = file.status === FILE_STATUS.PROCESSING;
                    return (
                      <div key={file.id} className={CLASSES.FILE}>
                        <div
                          className={`${CLASSES.FILE_INDICATOR} ${
                            isActive ? CLASSES.FILE_INDICATOR_ACTIVE : CLASSES.FILE_INDICATOR_QUEUED
                          }`}
                        >
                          {isActive ? <LoadingIcon /> : <span />}
                        </div>
                        <FileIcon />
                        {file.name}
                      </div>
                    );
                  })}
                </>
              )}
              {showLegacyGroupedList &&
                Object.keys(FILE_STATUSES).map((statusName) => {
                  if (isQueueV2 && showV2CompletedUi && statusName === FILE_STATUS.PROCESSING_SUCCESS) return null;
                  if (res?.data.findIndex((file) => file.status === statusName) === -1) return null;

                  const { oneWordText, title: groupTitle, fgColor, bgColor, Icon } =
                    FILE_STATUSES[statusName as CtxFileStatus];
                  return (
                    <div className={CLASSES.FILE_GROUP} key={statusName}>
                      {res && res.data.length > 0 && (
                        <div className={CLASSES.DISCLAIMER}>
                          {getIcon('error', {})}
                          <div className={`${CLASSES.DISCLAIMER}__text`}>
                            Please ensure the related experience is active before processing an upload
                          </div>
                        </div>
                      )}
                      <h5 style={{ color: fgColor }}>{groupTitle}</h5>
                      {res?.data
                        .filter((file) => file.status === statusName)
                        .map((file) => (
                          <div key={file.id} className={CLASSES.FILE}>
                            <FileIcon />
                            {file.name}
                            {file.status === FILE_STATUS.UPLOADED && !fileCounts.hasProcessing ? (
                              <ProcessContextFileButton fileId={file.id} />
                            ) : (
                              <div
                                className={CLASSES.ITEM_STATUS_LABEL_ROUNDED}
                                style={{ backgroundColor: bgColor, color: fgColor }}
                              >
                                <Icon />
                                {oneWordText}
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  );
                })}
              {res?.data.length === 0 && <div className={CLASSES.EMPTY_STATE}>No context data file yet</div>}
            </div>
            <div className={CLASSES.VIEW_ALL}>
              {isQueueV2 ? (
                <button
                  type="button"
                  className={CLASSES.VIEW_ALL_LINK}
                  onClick={handleViewAllClick}
                  onKeyDown={(evt: React.KeyboardEvent<HTMLButtonElement>) => {
                    if (evt.key === EVENT_KEYS.ENTER) handleViewAllClick();
                  }}
                >
                  Manage all files
                </button>
              ) : (
                <FlightButton
                  theme="minor"
                  label="View All"
                  onClick={handleViewAllClick}
                  onKeyDown={(evt: React.KeyboardEvent<HTMLButtonElement>) => {
                    if (evt.key === EVENT_KEYS.ENTER) handleViewAllClick();
                  }}
                />
              )}
            </div>
          </div>
        </FlightDropdown>
      </div>
    </div>
  );
};

export default FileUploadDropdown;
